// Language Tests — the section, not a card on someone's profile.
//
// One panel for both papers: who a test went to, when, and what they scored.
// Sending, amending and deleting all happen here, so the same action never has
// two homes; a student's profile shows their results read-only and links back.
//
// The one thing to keep straight while reading this file: `scoring` decides how
// a result reads. English passes or fails at 70%; French places a candidate on
// the CEFR scale and has no verdict at all. `resultText`/`resultTone` in
// lib/languageTests.js are the only places that branch on it.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertTriangle, Check, Copy, ExternalLink, GraduationCap, Loader2, Mail, Pencil,
  Plus, RotateCcw, Search, Send, Trash2, Users, X,
} from 'lucide-react';
import {
  deleteLanguageTest, getLanguageTest, getLanguageTestCatalog, getLanguageTests,
  sendLanguageTest, updateLanguageTestCopies, updateLanguageTestResult,
} from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { ADMIN_SCOPES, hasAdminScope } from '@/lib/auth';
import {
  DELIVERY_STATUS_LABELS, MAX_COPY_RECIPIENTS, RESULT_EMAIL_LABELS, TEST_STATUSES,
  copySummary, findTest, formatDateTime, invalidEmails, joinEmailList, levelOptions,
  parseEmailList, profilePathFor, resultText, resultTone, targetTypeMeta, testStatusMeta,
} from '@/lib/languageTests';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  StatusPill, TableFilterCard, adminInputClass, filterPillActiveClass, filterPillClass,
  tableCardClass,
} from '@/components/admin/AdminTableStyles';
import { cn } from '@/lib/utils';
import LanguageTestEditor from '@/components/admin/LanguageTestEditor';

const RESULT_TONE_CLASS = {
  good: 'text-emerald-700',
  warn: 'text-amber-700',
  neutral: 'text-[#062B1A]',
};

function SummaryTile({ label, value }) {
  return (
    <div className="rounded-xl border border-[rgba(6,43,26,0.08)] bg-white p-4">
      <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#062B1A]/45">
        {label}
      </p>
      <p className="mt-1 text-2xl font-semibold text-[#062B1A]">{value}</p>
    </div>
  );
}

/**
 * One box for a copy list. Staff separate addresses however they naturally
 * would, and anything unusable is called out inline rather than rejected on
 * save with a message about the whole field.
 */
function CopyField({ id, label, value, onChange, hint }) {
  const bad = invalidEmails(value);
  const good = parseEmailList(value);
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id} value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="agency@example.com, colleague@example.com"
        className={adminInputClass}
      />
      {hint && <p className="text-xs text-[#062B1A]/45">{hint}</p>}
      {bad.length > 0 && (
        <p className="text-xs text-[rgb(153,27,27)]">
          Not a valid address: {bad.join(', ')}
        </p>
      )}
      {good.length > MAX_COPY_RECIPIENTS && (
        <p className="text-xs text-[rgb(153,27,27)]">
          At most {MAX_COPY_RECIPIENTS} addresses.
        </p>
      )}
    </div>
  );
}

function Result({ row }) {
  if (row.status !== 'submitted') {
    return <span className="text-[#062B1A]/40">—</span>;
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn('font-semibold', RESULT_TONE_CLASS[resultTone(row)])}>
        {resultText(row)}
      </span>
      {row.overridden && (
        <StatusPill tone="gold" title={row.override_reason}>Amended</StatusPill>
      )}
    </span>
  );
}

export default function AdminLanguageTests() {
  const { profile } = useAuth();
  // Deleting destroys the only evidence someone sat a test, so the server asks
  // for full admin. The button is disabled rather than hidden, matching how the
  // rest of the admin app treats a permission you do not have.
  const canDelete = hasAdminScope(profile, ADMIN_SCOPES.ADMIN);

  const [catalog, setCatalog] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total: 0, submitted: 0, awaiting: 0, expired: 0 });
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState('');
  const [testCode, setTestCode] = useState('');
  const [status, setStatus] = useState('');

  const [sendOpen, setSendOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [amend, setAmend] = useState(null);
  const [copies, setCopies] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busy, setBusy] = useState(false);
  const [latestLink, setLatestLink] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await getLanguageTests({
        q: q || undefined,
        test_code: testCode || undefined,
        status: status || undefined,
        limit: 200,
      });
      setRows(data?.items || []);
      setSummary(data?.summary || { total: 0, submitted: 0, awaiting: 0, expired: 0 });
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Could not load language tests');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [q, testCode, status]);

  useEffect(() => {
    getLanguageTestCatalog()
      .then(({ data }) => setCatalog(data?.tests || []))
      .catch(console.error);
  }, []);

  // Debounced so typing in the search box does not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => { load(); }, q ? 300 : 0);
    return () => clearTimeout(timer);
  }, [load, q]);

  const openDetail = async (id) => {
    try {
      const { data } = await getLanguageTest(id);
      setDetail(data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not open this result');
    }
  };

  const remove = async () => {
    if (!confirmDelete) return;
    try {
      setBusy(true);
      await deleteLanguageTest(confirmDelete.id);
      setConfirmDelete(null);
      setDetail(null);
      await load();
      toast.success('Result deleted');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not delete this result');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1
            className="flex items-center gap-2 text-2xl font-semibold text-[#062B1A]"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            <GraduationCap className="size-6 text-[#D4AF37]" /> Language Tests
          </h1>
          <p className="mt-1 text-sm text-[#062B1A]/60">
            English proficiency and French placement. Results are emailed to the candidate
            automatically and appear on their profile when they have one.
          </p>
        </div>
        <Button
          onClick={() => { setLatestLink(''); setSendOpen(true); }}
          className="shrink-0 bg-[#062B1A] text-white hover:bg-[#041B10]"
        >
          <Send className="mr-2 size-4" /> Send a test
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <SummaryTile label="Total sent" value={summary.total} />
        <SummaryTile label="Submitted" value={summary.submitted} />
        <SummaryTile label="Awaiting" value={summary.awaiting} />
        <SummaryTile label="Expired" value={summary.expired} />
      </div>

      <LanguageTestEditor />

      <TableFilterCard>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#062B1A]/35" />
            <Input
              value={q}
              onChange={(event) => setQ(event.target.value)}
              placeholder="Search by name or email"
              aria-label="Search language tests"
              className={cn('pl-9', adminInputClass)}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button" size="sm" variant="outline"
              onClick={() => setTestCode('')}
              className={cn(filterPillClass, !testCode && filterPillActiveClass)}
            >
              All tests
            </Button>
            {catalog.map((test) => (
              <Button
                key={test.code} type="button" size="sm" variant="outline"
                onClick={() => setTestCode(test.code)}
                className={cn(filterPillClass, testCode === test.code && filterPillActiveClass)}
              >
                {test.short_label}
              </Button>
            ))}
          </div>
          <Select value={status || 'all'} onValueChange={(value) => setStatus(value === 'all' ? '' : value)}>
            <SelectTrigger aria-label="Status" className={cn('w-full lg:w-44', adminInputClass)}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any status</SelectItem>
              {TEST_STATUSES.map((value) => (
                <SelectItem key={value} value={value}>{testStatusMeta(value).label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </TableFilterCard>

      <Card className={tableCardClass}>
        <CardContent className="p-0">
          {loading ? (
            <div className="space-y-2 p-4">
              {[0, 1, 2, 3].map((row) => (
                <div key={row} className="h-11 animate-pulse rounded-lg bg-[#F7F8FA]" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <p className="px-4 py-14 text-center text-sm text-[#062B1A]/45">
              No language tests match these filters.
            </p>
          ) : (
            <div className="min-w-0 overflow-x-auto">
              <table className="w-full min-w-[900px] text-left text-sm">
                <thead className="bg-[#062B1A] text-[11px] uppercase tracking-[0.08em] text-white/85">
                  <tr>
                    <th className="px-4 py-3">Candidate</th>
                    <th className="px-3 py-3">Test</th>
                    <th className="px-3 py-3">Sent</th>
                    <th className="px-3 py-3">Status</th>
                    <th className="px-3 py-3">Result</th>
                    <th className="px-3 py-3">Profile</th>
                    <th className="px-3 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[rgba(6,43,26,0.07)]">
                  {rows.map((row) => {
                    const meta = testStatusMeta(row.status);
                    const profilePath = profilePathFor(row);
                    return (
                      <tr key={row.id} className="bg-white hover:bg-[#F7F8FA]/80">
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => openDetail(row.id)}
                            className="text-left font-semibold text-[#062B1A] hover:underline"
                          >
                            {row.recipient_name || '—'}
                          </button>
                          <p className="text-xs text-[#062B1A]/50">{row.recipient_email}</p>
                        </td>
                        <td className="px-3 py-3">
                          <p className="text-[#062B1A]/80">{row.test_short_label}</p>
                          <p className="text-xs text-[#062B1A]/45">Attempt #{row.attempt_number}</p>
                        </td>
                        <td className="px-3 py-3 text-[#062B1A]/70">
                          <p>{formatDateTime(row.sent_at)}</p>
                          <p className="text-xs text-[#062B1A]/45">
                            {DELIVERY_STATUS_LABELS[row.delivery_status] || row.delivery_status}
                            {copySummary(row) ? ` · ${copySummary(row)}` : ''}
                          </p>
                        </td>
                        <td className="px-3 py-3">
                          <StatusPill tone={meta.pillTone}>{meta.label}</StatusPill>
                        </td>
                        <td className="px-3 py-3"><Result row={row} /></td>
                        <td className="px-3 py-3">
                          {profilePath ? (
                            <Link
                              to={profilePath}
                              className="inline-flex items-center gap-1 text-[#062B1A] hover:underline"
                            >
                              {targetTypeMeta(row.linked_type).label}
                              <ExternalLink className="size-3" />
                            </Link>
                          ) : (
                            <span className="text-xs text-[#062B1A]/40">Not in the portal</span>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex justify-end gap-1">
                            <Button
                              type="button" size="icon" variant="ghost"
                              aria-label={`Change who is copied for ${row.recipient_name}`}
                              title="Change who is copied on the result"
                              onClick={() => setCopies(row)}
                              className="size-8 text-[#062B1A]"
                            >
                              <Users className="size-3.5" />
                            </Button>
                            <Button
                              type="button" size="icon" variant="ghost"
                              aria-label={`Amend the result for ${row.recipient_name}`}
                              disabled={row.status !== 'submitted'}
                              title={row.status === 'submitted'
                                ? 'Amend this result'
                                : 'Only a submitted result can be amended'}
                              onClick={() => setAmend({ row, score: String(row.score ?? ''), level: '', reason: '' })}
                              className="size-8 text-[#062B1A]"
                            >
                              <Pencil className="size-3.5" />
                            </Button>
                            <Button
                              type="button" size="icon" variant="ghost"
                              aria-label={`Delete the result for ${row.recipient_name}`}
                              disabled={!canDelete}
                              title={canDelete
                                ? 'Delete this result'
                                : 'Only a full administrator can delete a result'}
                              onClick={() => setConfirmDelete(row)}
                              className="size-8 text-[rgb(153,27,27)]"
                            >
                              <Trash2 className="size-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <SendDialog
        open={sendOpen}
        catalog={catalog}
        latestLink={latestLink}
        onLink={setLatestLink}
        onClose={() => setSendOpen(false)}
        onSent={load}
      />

      <DetailDialog
        detail={detail}
        onClose={() => setDetail(null)}
        onAmend={(row) => setAmend({ row, score: String(row.score ?? ''), level: '', reason: '' })}
      />

      <AmendDialog
        state={amend}
        catalog={catalog}
        onClose={() => setAmend(null)}
        onSaved={async () => { setAmend(null); await load(); if (detail) openDetail(detail.id); }}
      />

      <CopiesDialog
        row={copies}
        onClose={() => setCopies(null)}
        onSaved={async () => { setCopies(null); await load(); if (detail) openDetail(detail.id); }}
      />

      <Dialog open={!!confirmDelete} onOpenChange={(open) => !open && setConfirmDelete(null)}>
        <DialogContent className="sm:max-w-md" data-testid="confirm-delete-language-test">
          <DialogHeader>
            <DialogTitle>Delete this result?</DialogTitle>
            <DialogDescription>
              This permanently removes {confirmDelete?.recipient_name}&rsquo;s{' '}
              {confirmDelete?.test_short_label} attempt and invalidates their link. If they had
              already submitted, the score and every answer go with it. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button
              disabled={busy} onClick={remove}
              className="bg-[rgb(153,27,27)] text-white hover:bg-[rgb(127,22,22)]"
            >
              {busy ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Trash2 className="mr-2 size-4" />}
              Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Send a test to a record, or to a name and an email that is not one yet. */
function SendDialog({ open, catalog, latestLink, onLink, onClose, onSent }) {
  const [testCode, setTestCode] = useState('');
  const [mode, setMode] = useState('standalone');
  const [targetType, setTargetType] = useState('student');
  const [targetId, setTargetId] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTestCode((current) => current || catalog[0]?.code || '');
  }, [open, catalog]);

  const test = findTest(catalog, testCode);

  const submit = async () => {
    try {
      setSending(true);
      const base = mode === 'record'
        ? { test_code: testCode, target_type: targetType, target_id: targetId.trim() }
        : { test_code: testCode, target_type: 'standalone', recipient_name: name.trim(), recipient_email: email.trim() };
      const { data } = await sendLanguageTest({
        ...base, cc: parseEmailList(cc), bcc: parseEmailList(bcc),
      });
      onLink(data?.url || '');
      await onSent();
      toast.success(data?.assessment?.delivery_status === 'emailed'
        ? 'Test emailed to the candidate'
        : 'Test created — copy the secure link to share it');
      setName(''); setEmail(''); setTargetId(''); setCc(''); setBcc('');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(
        Array.isArray(detail) ? detail.map((item) => item.msg || item).join(' ')
          : typeof detail === 'string' ? detail : 'Could not send this test',
      );
    } finally {
      setSending(false);
    }
  };

  const copiesValid = invalidEmails(cc).length === 0 && invalidEmails(bcc).length === 0;
  const ready = testCode && copiesValid
    && (mode === 'record' ? targetId.trim() : name.trim() && email.trim());

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Send a language test</DialogTitle>
          <DialogDescription>
            The candidate gets a secure single-use link by email, and their result the moment
            they submit.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="lt-test">Test</Label>
            <Select value={testCode} onValueChange={setTestCode}>
              <SelectTrigger id="lt-test" aria-label="Test" className={adminInputClass}>
                <SelectValue placeholder="Choose a test" />
              </SelectTrigger>
              <SelectContent>
                {catalog.map((item) => (
                  <SelectItem key={item.code} value={item.code}>{item.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {test && <p className="text-xs text-[#062B1A]/50">{test.description}</p>}
          </div>

          <div className="space-y-1.5">
            <Label>Send to</Label>
            <div className="flex gap-2">
              <Button
                type="button" size="sm" variant="outline"
                aria-pressed={mode === 'record'}
                onClick={() => setMode('record')}
                className={cn(filterPillClass, mode === 'record' && filterPillActiveClass)}
              >
                An existing student or applicant
              </Button>
              <Button
                type="button" size="sm" variant="outline"
                aria-pressed={mode === 'standalone'}
                onClick={() => setMode('standalone')}
                className={cn(filterPillClass, mode === 'standalone' && filterPillActiveClass)}
              >
                Someone else
              </Button>
            </div>
          </div>

          {mode === 'record' ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[160px_1fr]">
              <div className="space-y-1.5">
                <Label htmlFor="lt-target-type">Record type</Label>
                <Select value={targetType} onValueChange={setTargetType}>
                  <SelectTrigger id="lt-target-type" aria-label="Record type" className={adminInputClass}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="student">Student</SelectItem>
                    <SelectItem value="application">Applicant</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="lt-target-id">Record id</Label>
                <Input
                  id="lt-target-id" value={targetId}
                  onChange={(event) => setTargetId(event.target.value)}
                  placeholder="Paste the student or application id"
                  className={cn('font-mono text-xs', adminInputClass)}
                />
                <p className="text-xs text-[#062B1A]/45">
                  The name and email are read off the record, so the test always reaches the
                  person whose profile it lands on.
                </p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="lt-name">Name</Label>
                <Input
                  id="lt-name" value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Jean Tremblay" className={adminInputClass}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="lt-email">Email</Label>
                <Input
                  id="lt-email" type="email" value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="jean@example.com" className={adminInputClass}
                />
              </div>
              <p className="text-xs text-[#062B1A]/45 sm:col-span-2">
                If this address matches a student or applicant — now or later — the result shows
                on their profile automatically.
              </p>
            </div>
          )}

          <div className="space-y-3 rounded-lg border border-[rgba(6,43,26,0.10)] p-3">
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/50">
              <Users className="size-3.5" /> Copy on the result
            </p>
            <CopyField
              id="lt-cc" label="Cc" value={cc} onChange={setCc}
              hint="A referring agency, an admissions colleague. They see each other's addresses."
            />
            <CopyField
              id="lt-bcc" label="Bcc" value={bcc} onChange={setBcc}
              hint="Copied without the other recipients seeing them."
            />
            {/* Saying this out loud, because "cc" normally means "on everything"
                and here it deliberately does not. */}
            <p className="rounded bg-[#062B1A]/[0.04] p-2 text-xs text-[#062B1A]/60">
              These addresses receive the <strong>result</strong> only. The invitation is not
              copied to anyone: it carries the secure link, and whoever holds that link can sit
              the test as the candidate.
            </p>
          </div>

          {latestLink && (
            <div className="flex flex-col gap-2 rounded-lg border border-[#D4AF37]/40 bg-[#D4AF37]/8 p-3 sm:flex-row sm:items-center">
              <p className="min-w-0 flex-1 truncate text-xs text-[#062B1A]/70">{latestLink}</p>
              <Button
                type="button" size="sm" variant="outline"
                onClick={async () => {
                  await navigator.clipboard.writeText(latestLink);
                  toast.success('Secure link copied');
                }}
              >
                <Copy className="mr-2 size-3.5" />Copy link
              </Button>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button
            disabled={sending || !ready} onClick={submit}
            className="bg-[#062B1A] text-white hover:bg-[#041B10]"
          >
            {sending ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Plus className="mr-2 size-4" />}
            Send test
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** One result in full, with per-question marking once it has been submitted. */
function DetailDialog({ detail, onClose, onAmend }) {
  if (!detail) return null;
  const profilePath = profilePathFor(detail);
  const placement = detail.scoring === 'placement';

  return (
    <Dialog open={!!detail} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{detail.recipient_name}</DialogTitle>
          <DialogDescription>
            {detail.test_label} · attempt #{detail.attempt_number} · {detail.recipient_email}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <SummaryTile label="Status" value={testStatusMeta(detail.status).label} />
            <SummaryTile label="Score" value={detail.status === 'submitted' ? `${detail.score}/${detail.max_score}` : '—'} />
            <SummaryTile
              label={placement ? 'Level' : 'Result'}
              value={detail.status === 'submitted'
                ? (placement ? (detail.level_label || '—') : (detail.passed ? 'Pass' : 'Not passed'))
                : '—'}
            />
            <SummaryTile label="Percentage" value={detail.status === 'submitted' ? `${detail.percentage}%` : '—'} />
          </div>

          {detail.level_description && (
            <p className="rounded-lg border border-[#D4AF37]/40 bg-[#D4AF37]/8 p-3 text-sm text-[#062B1A]/75">
              {detail.level_description}
            </p>
          )}

          {detail.overridden && (
            <div className="rounded-lg border border-[#D4AF37]/45 bg-[#D4AF37]/8 p-3 text-sm">
              <p className="flex items-center gap-2 font-semibold text-[#8A6508]">
                <AlertTriangle className="size-4" /> This result was amended
              </p>
              <p className="mt-1 text-[#062B1A]/75">
                Automatically marked {detail.auto_score}/{detail.max_score}
                {detail.auto_level_label ? ` · ${detail.auto_level_label}` : ''}
                {detail.auto_passed !== null && detail.auto_passed !== undefined && !placement
                  ? ` · ${detail.auto_passed ? 'Pass' : 'Not passed'}` : ''}.
              </p>
              <p className="mt-1 text-[#062B1A]/75">
                &ldquo;{detail.override_reason}&rdquo; — {detail.overridden_by_name},{' '}
                {formatDateTime(detail.overridden_at)}
              </p>
            </div>
          )}

          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
            {[
              ['Sent', formatDateTime(detail.sent_at)],
              ['Started', formatDateTime(detail.started_at)],
              ['Submitted', formatDateTime(detail.submitted_at)],
              ['Link expires', formatDateTime(detail.expires_at)],
              ['Sent by', detail.sent_by_name || '—'],
              ['Invitation', DELIVERY_STATUS_LABELS[detail.delivery_status] || '—'],
              ['Result email', RESULT_EMAIL_LABELS[detail.result_email_status] ?? '—'],
              ['Cc', detail.cc?.length ? detail.cc.join(', ') : '—'],
              ['Bcc', detail.bcc?.length ? detail.bcc.join(', ') : '—'],
              ['Profile', profilePath ? targetTypeMeta(detail.linked_type).label : 'Not in the portal'],
            ].map(([label, value]) => (
              <div key={label}>
                <dt className="text-xs uppercase tracking-wide text-[#062B1A]/45">{label}</dt>
                <dd className="text-[#062B1A]/80">{value}</dd>
              </div>
            ))}
          </dl>

          {detail.by_level?.length > 0 && (
            <div>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#062B1A]/45">
                By level — where they stopped coping
              </p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {detail.by_level.map((band) => (
                  <div key={band.name} className="rounded-lg border border-[rgba(6,43,26,0.10)] p-3">
                    <p className="text-xs font-semibold text-[#062B1A]">{band.name}</p>
                    <p className="text-lg font-semibold text-[#062B1A]">
                      {band.correct}/{band.asked}
                    </p>
                    <p className="text-xs text-[#062B1A]/45">{band.percentage}%</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {detail.by_skill?.length > 0 && (
            <div>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#062B1A]/45">
                By skill
              </p>
              <div className="flex flex-wrap gap-2">
                {detail.by_skill.map((skill) => (
                  <StatusPill key={skill.name} tone="neutral">
                    {skill.name}: {skill.correct}/{skill.asked}
                  </StatusPill>
                ))}
              </div>
            </div>
          )}

          {detail.breakdown?.length > 0 && (
            <div>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#062B1A]/45">
                Every question
              </p>
              <div className="space-y-2">
                {detail.breakdown.map((row) => (
                  <div
                    key={row.id}
                    className="rounded-lg border border-[rgba(6,43,26,0.10)] p-3 text-sm"
                  >
                    <div className="flex items-start gap-2">
                      {row.correct
                        ? <Check className="mt-0.5 size-4 shrink-0 text-emerald-600" />
                        : <X className="mt-0.5 size-4 shrink-0 text-red-600" />}
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-[#062B1A]">
                          {row.number}. {row.text}
                        </p>
                        <p className="mt-1 text-xs text-[#062B1A]/60">
                          Answered: {row.answered ? row.chosen_option : <em>no answer</em>}
                        </p>
                        {!row.correct && (
                          <p className="text-xs text-emerald-700">Correct: {row.correct_option}</p>
                        )}
                      </div>
                      {row.level && <StatusPill tone="neutral">{row.level}</StatusPill>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          {profilePath && (
            <Button variant="outline" asChild>
              <Link to={profilePath}><ExternalLink className="mr-2 size-4" />Open profile</Link>
            </Button>
          )}
          <Button
            variant="outline"
            disabled={detail.status !== 'submitted'}
            onClick={() => onAmend(detail)}
          >
            <Pencil className="mr-2 size-4" />Amend result
          </Button>
          <Button onClick={onClose} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Change who is copied on the result.
 *
 * Separate from amending a mark on purpose: adding a referring agency is not a
 * correction and should not want a reason. Useful right up until the candidate
 * submits — after that the result has already gone, and the dialog says so
 * rather than letting staff believe a late addition will receive it.
 */
function CopiesDialog({ row, onClose, onSaved }) {
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!row) return;
    setCc(joinEmailList(row.cc));
    setBcc(joinEmailList(row.bcc));
  }, [row]);

  if (!row) return null;
  const alreadySent = row.status === 'submitted';
  const valid = invalidEmails(cc).length === 0 && invalidEmails(bcc).length === 0;

  const save = async () => {
    try {
      setBusy(true);
      await updateLanguageTestCopies(row.id, {
        cc: parseEmailList(cc), bcc: parseEmailList(bcc),
      });
      await onSaved();
      toast.success(alreadySent ? 'Copy list updated' : 'They will be copied on the result');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(
        Array.isArray(detail) ? detail.map((item) => item.msg || item).join(' ')
          : typeof detail === 'string' ? detail : 'Could not update the copy list',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!row} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Who is copied on {row.recipient_name}&rsquo;s result?</DialogTitle>
          <DialogDescription>
            {row.test_label}. The candidate is always the main recipient; these addresses
            receive a copy of their result.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {alreadySent && (
            <p className="flex items-start gap-2 rounded-lg border border-[#D4AF37]/45 bg-[#D4AF37]/8 p-3 text-sm text-[#062B1A]/80">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-[#8A6508]" />
              This result has already been sent. Changing the list keeps the record accurate,
              but will not send it again.
            </p>
          )}
          <CopyField
            id="lt-copies-cc" label="Cc" value={cc} onChange={setCc}
            hint="They see each other's addresses."
          />
          <CopyField
            id="lt-copies-bcc" label="Bcc" value={bcc} onChange={setBcc}
            hint="Copied without the other recipients seeing them."
          />
          <p className="rounded bg-[#062B1A]/[0.04] p-2 text-xs text-[#062B1A]/60">
            The result only. The invitation is never copied to anyone — it carries the secure
            link, and whoever holds that link can sit the test as the candidate.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            disabled={busy || !valid} onClick={save}
            className="bg-[#062B1A] text-white hover:bg-[#041B10]"
          >
            {busy ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Users className="mr-2 size-4" />}
            Save copy list
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Amend a marked result.
 *
 * The automatic marking is never overwritten — it is kept beside the amendment
 * so the panel can show both and this dialog can put it back. A reason is
 * required, because a score nobody can explain is worse than a wrong one.
 */
function AmendDialog({ state, catalog, onClose, onSaved }) {
  const [score, setScore] = useState('');
  const [level, setLevel] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const row = state?.row;
  useEffect(() => {
    if (!state) return;
    setScore(String(row?.score ?? ''));
    setLevel('');
    setReason('');
  }, [state, row]);

  const test = findTest(catalog, row?.test_code);
  const placement = row?.scoring === 'placement';
  const levels = useMemo(() => levelOptions(test), [test]);

  if (!state) return null;

  const save = async (payload) => {
    try {
      setBusy(true);
      await updateLanguageTestResult(row.id, payload);
      await onSaved();
      toast.success(payload.revert ? 'Automatic marking restored' : 'Result amended');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(
        Array.isArray(detail) ? detail.map((item) => item.msg || item).join(' ')
          : typeof detail === 'string' ? detail : 'Could not amend this result',
      );
    } finally {
      setBusy(false);
    }
  };

  const changed = String(row?.score ?? '') !== score.trim() || !!level;

  return (
    <Dialog open={!!state} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Amend {row?.recipient_name}&rsquo;s result</DialogTitle>
          <DialogDescription>
            {row?.test_label}. The automatic marking is kept and can be restored at any time.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg bg-[#F7F8FA] p-3 text-sm text-[#062B1A]/70">
            Automatically marked{' '}
            <strong>{row?.overridden ? row?.auto_score : row?.score}/{row?.max_score}</strong>
            {row?.overridden && row?.auto_level_label ? ` · ${row.auto_level_label}` : ''}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="lt-score">Score</Label>
            <div className="flex items-center gap-2">
              <Input
                id="lt-score" type="number" min={0} max={row?.max_score}
                value={score} onChange={(event) => setScore(event.target.value)}
                className={cn('w-28', adminInputClass)}
              />
              <span className="text-sm text-[#062B1A]/55">out of {row?.max_score}</span>
            </div>
            <p className="text-xs text-[#062B1A]/45">
              {placement
                ? 'The CEFR level is re-derived from this score unless you pin one below.'
                : 'Pass or fail is re-derived from this score.'}
            </p>
          </div>

          {placement && (
            <div className="space-y-1.5">
              <Label htmlFor="lt-level">Level (optional)</Label>
              <Select value={level || 'auto'} onValueChange={(value) => setLevel(value === 'auto' ? '' : value)}>
                <SelectTrigger id="lt-level" aria-label="Level" className={adminInputClass}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Derive from the score</SelectItem>
                  {levels.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#062B1A]/45">
                Pin a level to place a candidate against what the score alone says — after an oral
                interview, for instance.
              </p>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="lt-reason">Reason <span className="text-[rgb(153,27,27)]">*</span></Label>
            <Textarea
              id="lt-reason" rows={2} value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="e.g. Q14 was mis-keyed and the paper was re-marked by hand"
              className={adminInputClass}
            />
            <p className="text-xs text-[#062B1A]/45">
              Recorded against the result with your name, and shown wherever it appears.
            </p>
          </div>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
          <Button
            type="button" variant="outline"
            disabled={busy || !row?.overridden}
            title={row?.overridden ? 'Restore the automatic marking' : 'This result has not been amended'}
            onClick={() => save({ revert: true })}
          >
            <RotateCcw className="mr-2 size-4" />Restore automatic marking
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              disabled={busy || !reason.trim() || !changed}
              onClick={() => save({
                ...(String(row?.score ?? '') === score.trim() ? {} : { score: Number(score) }),
                ...(level ? { level } : {}),
                reason: reason.trim(),
              })}
              className="bg-[#062B1A] text-white hover:bg-[#041B10]"
            >
              {busy ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Mail className="mr-2 size-4" />}
              Save amendment
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
